// src/components/BusinessSettings.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
// Firebase helpers
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { getStorage, ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { app, db } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';

const DAY_NAME_TO_INDEX = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

export default function BusinessSettings() {
  const [tabIndex, setTabIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  // removed dead state: businessEndTime, breakTypes, newBreak*, loading
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  // Company general settings
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('United States of America');
  const [startWeekOn, setStartWeekOn] = useState('Monday');
  const [mobileSmsNotifications, setMobileSmsNotifications] = useState(false);
  const [contactPageEnabled, setContactPageEnabled] = useState(true);
  const [accountCreatorContactInfo, setAccountCreatorContactInfo] = useState(true);
  // preferences removed (unused)
  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Availability settings (company-level)
  const [temporaryAvailability, setTemporaryAvailability] = useState(false);
  const [allowSubmissionsForPublishedSchedules, setAllowSubmissionsForPublishedSchedules] = useState(false);
  const [availabilityVisibleToEmployees, setAvailabilityVisibleToEmployees] = useState(true);
  const [availabilityApprovals, setAvailabilityApprovals] = useState(false);
  // Payroll settings (tenant-level)
  const [payFrequency, setPayFrequency] = useState('biweekly');
  const [payrollWeekStartDay, setPayrollWeekStartDay] = useState(1); // 0=Sun ... 6=Sat
  const [firstPeriodStartDate, setFirstPeriodStartDate] = useState('');
  const [payrollError, setPayrollError] = useState('');

  const toDateInputValue = (value) => {
    if (!value) return '';
    const d = value?.toDate ? value.toDate() : (value instanceof Date ? value : new Date(value));
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Handlers (minimal implementations)
  const handleLogoSelected = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      // upload to Firebase Storage under tenants/{tenantId}/company/
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Tenant not resolved');
      const storage = getStorage(app);
      const ext = (file.name && file.name.split('.').pop()) || 'png';
      const path = `tenants/${tenantId}/company/logo-${Date.now()}.${ext}`;
      const ref = sRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);

      // persist url to businessSettings/current (merge)
      const docRef = doc(db, `tenants/${tenantId}/businessSettings`, 'current');
      await setDoc(docRef, { company: { logoUrl: url } }, { merge: true });

      // update local preview
      setLogoUrl(url);
      setNotification({ open: true, message: 'Logo uploaded', severity: 'success' });
    } catch (e) {
      console.error('logo upload failed', e);
      setNotification({ open: true, message: `Logo upload failed: ${e?.message || e}`, severity: 'error' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl('');
  };

  const handleSave = async () => {
    setSaving(true);
    setPayrollError('');
    try {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Tenant not resolved');
      const businessDocRef = doc(db, `tenants/${tenantId}/businessSettings`, 'current');
      const payrollDocRef = doc(db, `tenants/${tenantId}/settings`, 'payroll');

      if (!firstPeriodStartDate) {
        throw new Error('Payroll start date is required');
      }
      const parsedStartDate = new Date(`${firstPeriodStartDate}T00:00:00`);
      if (Number.isNaN(parsedStartDate.getTime())) {
        throw new Error('Payroll start date is invalid');
      }

      // persist basic company info (merge)
      await setDoc(businessDocRef, {
        company: {
          name: companyName || '',
          country: country || '',
          startWeekOn: startWeekOn || '',
          mobileSmsNotifications: !!mobileSmsNotifications,
          contactPageEnabled: !!contactPageEnabled,
          accountCreatorContactInfo: !!accountCreatorContactInfo,
          logoUrl: logoUrl || '',
          availability: {
            temporaryAvailability: !!temporaryAvailability,
            allowSubmissionsForPublishedSchedules: !!allowSubmissionsForPublishedSchedules,
            availabilityVisibleToEmployees: !!availabilityVisibleToEmployees,
            availabilityApprovals: !!availabilityApprovals,
          },
        }
      }, { merge: true });

      // Persist payroll settings (separate doc from company profile settings)
      await setDoc(payrollDocRef, {
        payFrequency: payFrequency || 'biweekly',
        weekStartDay: Number(payrollWeekStartDay),
        firstPeriodStartDate: Timestamp.fromDate(parsedStartDate),
        updatedAt: Timestamp.now(),
      }, { merge: true });

      setNotification({ open: true, message: 'Settings saved', severity: 'success' });
    } catch (e) {
      console.error('failed to save business settings:', e);
      setPayrollError(e?.message || 'Error saving payroll settings');
      setNotification({ open: true, message: `Error saving business settings: ${e?.message || e}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };


  // Load saved settings on mount
  useEffect(() => {
    let mounted = true;
    const loadSettings = async () => {
      try {
        const tenantId = await tryResolveTenantId();
        if (!tenantId) {
          setNotification({ open: true, message: 'Tenant not resolved. Please sign in as an admin.', severity: 'error' });
          return;
        }
        const ref = doc(db, `tenants/${tenantId}/businessSettings`, 'current');
        const payrollRef = doc(db, `tenants/${tenantId}/settings`, 'payroll');
        const snap = await getDoc(ref);
        const payrollSnap = await getDoc(payrollRef);
        const data = snap.exists() ? (snap.data() || {}) : {};
        const company = data.company || {};
        const availability = company.availability || data.availability || {};

        if (!mounted) return;

        setCompanyName(company.name || '');
        setCountry(company.country || 'United States of America');
        setStartWeekOn(company.startWeekOn || 'Monday');
        setMobileSmsNotifications(!!company.mobileSmsNotifications);
        setContactPageEnabled(company.contactPageEnabled !== false);
        setAccountCreatorContactInfo(company.accountCreatorContactInfo !== false);
        setLogoUrl(company.logoUrl || '');

        setTemporaryAvailability(!!availability.temporaryAvailability);
        setAllowSubmissionsForPublishedSchedules(!!availability.allowSubmissionsForPublishedSchedules);
        setAvailabilityVisibleToEmployees(availability.availabilityVisibleToEmployees !== false);
        setAvailabilityApprovals(!!availability.availabilityApprovals);

        const payroll = payrollSnap.exists() ? (payrollSnap.data() || {}) : {};
        setPayFrequency(payroll.payFrequency || 'biweekly');
        if (typeof payroll.weekStartDay === 'number') {
          setPayrollWeekStartDay(payroll.weekStartDay);
        } else {
          setPayrollWeekStartDay(DAY_NAME_TO_INDEX[company.startWeekOn || 'Monday'] ?? 1);
        }
        setFirstPeriodStartDate(toDateInputValue(payroll.firstPeriodStartDate));

      } catch (e) {
        console.error('BusinessSettings: failed to load settings', e);
        setNotification({ open: true, message: 'Failed to load business settings', severity: 'error' });
      }
    };

    loadSettings();
    return () => { mounted = false; };
  }, []);
  return (
    <Paper sx={{ p: 3 }}>
      <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 3 }}>
        <Tab label="General" />
  <Tab label="Availability" />
        <Tab label="Payroll" />
      </Tabs>

      {/* General tab */}
      {tabIndex === 0 && (
        <Box component={Paper} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Avatar src={logoUrl} sx={{ width: 64, height: 64, bgcolor: 'background.default' }} />
              <Box>
                <Button variant="outlined" component="label" disabled={uploadingLogo}>
                  {uploadingLogo ? <><CircularProgress size={16} /> &nbsp;Uploading...</> : 'Upload photo'}
                  <input hidden accept="image/*" type="file" onChange={(e)=>handleLogoSelected(e.target.files && e.target.files[0])} />
                </Button>
                {logoUrl && <Button color="error" onClick={handleRemoveLogo} sx={{ ml: 1 }}>Remove</Button>}
              </Box>
            </Box>
            
          <TextField label="Company name" value={companyName} onChange={e=>setCompanyName(e.target.value)} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Country</InputLabel>
            <Select value={country} label="Country" onChange={e=>setCountry(e.target.value)}>
              <MenuItem value="United States of America">United States of America</MenuItem>
              <MenuItem value="Canada">Canada</MenuItem>
              <MenuItem value="United Kingdom">United Kingdom</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ width: 300, mt: 2 }} size="small">
            <InputLabel>Start week on</InputLabel>
            <Select value={startWeekOn} label="Start week on" onChange={e=>setStartWeekOn(e.target.value)}>
              <MenuItem value="Sunday">Sunday</MenuItem>
              <MenuItem value="Monday">Monday</MenuItem>
              <MenuItem value="Tuesday">Tuesday</MenuItem>
              <MenuItem value="Wednesday">Wednesday</MenuItem>
              <MenuItem value="Thursday">Thursday</MenuItem>
              <MenuItem value="Friday">Friday</MenuItem>
              <MenuItem value="Saturday">Saturday</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>Applies globally to all features and locations.</Typography>

          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={mobileSmsNotifications} onChange={e=>setMobileSmsNotifications(e.target.checked)} />}
              label={<>
                <Typography sx={{ fontWeight: 600 }}>Mobile SMS notifications</Typography>
                <Typography variant="body2" color="text.secondary">Allow SMS notifications for mobile numbers in this country. To switch between SMS and push, employees can head to notification settings in the mobile app.</Typography>
              </>}
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={contactPageEnabled} onChange={e=>setContactPageEnabled(e.target.checked)} />}
              label={<>
                <Typography sx={{ fontWeight: 600 }}>Contact page</Typography>
                <Typography variant="body2" color="text.secondary">Enable or disable the ability for employees to view each others contact information.</Typography>
              </>}
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={accountCreatorContactInfo} onChange={e=>setAccountCreatorContactInfo(e.target.checked)} />}
              label={<>
                <Typography sx={{ fontWeight: 600 }}>Account creator contact info</Typography>
                <Typography variant="body2" color="text.secondary">Show the admins (you) on the contact page.</Typography>
              </>}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</Button>
          </Box>

          </Box>
        </Box>
      )}

      

      

  {tabIndex === 1 && (
        <Box component={Paper} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
    <Typography variant="h6" gutterBottom color="primary.main" sx={{ mb: 1, fontWeight: 700, fontSize: '1.25rem' }}>Availability</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Configure how employees can submit availability and whether approvals are required.</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <FormControlLabel
                control={<Checkbox checked={temporaryAvailability} onChange={e=>setTemporaryAvailability(e.target.checked)} />}
                label={<><strong>Temporary Availability</strong><Typography variant="body2" color="text.secondary">Allow employees to fill in availability ranges for a temporary timeframe. If this is disabled, they will only be able to fill out recurring availability.</Typography></>}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={<Checkbox checked={allowSubmissionsForPublishedSchedules} onChange={e=>setAllowSubmissionsForPublishedSchedules(e.target.checked)} />}
                label={<><strong>Allow submissions for published schedules</strong><Typography variant="body2" color="text.secondary">If checked, employees can submit or change their weekly availability for weeks where a schedule has been published.</Typography></>}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={<Checkbox checked={availabilityVisibleToEmployees} onChange={e=>setAvailabilityVisibleToEmployees(e.target.checked)} />}
                label={<><strong>Availability visible to employees</strong><Typography variant="body2" color="text.secondary">If disabled, only managers will see the availability tab.</Typography></>}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={<Checkbox checked={availabilityApprovals} onChange={e=>setAvailabilityApprovals(e.target.checked)} />}
                label={<><strong>Availability approvals</strong><Typography variant="body2" color="text.secondary">If checked, any availability posted by employees would require manager approval.</Typography></>}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</Button>
          </Box>
        </Box>
      )}

      {tabIndex === 2 && (
        <Box component={Paper} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom color="primary.main" sx={{ mb: 1, fontWeight: 700, fontSize: '1.25rem' }}>
            Payroll
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure your payroll period generation settings.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
            <FormControl fullWidth>
              <InputLabel>Pay frequency</InputLabel>
              <Select
                value={payFrequency}
                label="Pay frequency"
                onChange={(e) => setPayFrequency(e.target.value)}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Biweekly</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Payroll week starts on</InputLabel>
              <Select
                value={payrollWeekStartDay}
                label="Payroll week starts on"
                onChange={(e) => setPayrollWeekStartDay(Number(e.target.value))}
              >
                <MenuItem value={0}>Sunday</MenuItem>
                <MenuItem value={1}>Monday</MenuItem>
                <MenuItem value={2}>Tuesday</MenuItem>
                <MenuItem value={3}>Wednesday</MenuItem>
                <MenuItem value={4}>Thursday</MenuItem>
                <MenuItem value={5}>Friday</MenuItem>
                <MenuItem value={6}>Saturday</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="First payroll period start date"
              type="date"
              value={firstPeriodStartDate}
              onChange={(e) => setFirstPeriodStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            {payrollError ? <Alert severity="error">{payrollError}</Alert> : null}

            <Box sx={{ mt: 1 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {notification.open && (
        <Alert 
          severity={notification.severity} 
          sx={{ mt: 2 }}
          onClose={() => setNotification({ open: false, message: '', severity: 'success' })}
        >
          {notification.message}
        </Alert>
      )}
    </Paper>
  );
}
